import { useEffect } from 'react';

import { Button } from '@mui/material';
import Container from '@mui/material/Container';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import { getIssuedChequeById } from 'src/redux/slices/issued-cheque';

import ErrorContent from 'src/components/error-content';
import { useSettingsContext } from 'src/components/settings';
import { LoadingScreen } from 'src/components/loading-screen';
import { LeftIcon } from 'src/components/carousel/arrow-icons';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';

import IssuedChequeDetails from '../issued-cheque-details';

type Props = {
  id: string;
};

export default function IssuedChequeDetailView({ id }: Props) {
  const { item, itemStatus, itemError } = useAppSelector((state) => state.issuedCheque);

  const settings = useSettingsContext();
  const dispatch = useAppDispatcher();

  useEffect(() => {
    dispatch(getIssuedChequeById({ id }));
  }, [dispatch, id]);

  // Another cheque may still be in the store from a previous visit.
  if (itemStatus === 'loading' || itemStatus === 'idle' || item?._id !== id) {
    if (itemStatus === 'failure') {
      return (
        <ErrorContent
          filled
          title="Failed to load cheque details"
          description={itemError}
          action={
            <Button
              component={RouterLink}
              href={paths.dashboard.issuedCheque.root}
              variant="contained"
              startIcon={<LeftIcon />}
            >
              Back to Issued Cheques
            </Button>
          }
          sx={{ py: 10 }}
        />
      );
    }

    return <LoadingScreen />;
  }

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Issued Cheque Details"
        links={[
          { name: 'Issued Cheques', href: paths.dashboard.issuedCheque.root },
          { name: 'Details' },
        ]}
        sx={{ mb: { xs: 3, md: 5 } }}
      />

      <IssuedChequeDetails cheque={item} />
    </Container>
  );
}
